import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Hash, 
  Copy, 
  Download, 
  Upload, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  FileText,
  Key,
  AlertTriangle,
  Info,
  Eye,
  EyeOff,
  Trash2,
  Shield,
  Clock,
  Database,
  Code,
  ArrowLeftRight,
  Search,
  Lock,
  CheckCircle,
  Bitcoin,
  Zap
} from 'lucide-react';

interface SHA256Settings {
  inputFormat: InputFormat;
  outputFormat: OutputFormat;
  showProgress: boolean;
  enableComparison: boolean;
  showAnalysis: boolean;
  autoGenerate: boolean;
  caseSensitive: boolean;
  includeTimestamp: boolean;
  showSecurityInfo: boolean;
  enableSalting: boolean;
  saltValue: string;
  iterations: number;
  showBlockchainMode: boolean;
}

interface HashResult {
  id: string;
  input: string;
  hash: string;
  inputFormat: InputFormat;
  outputFormat: OutputFormat;
  timestamp: Date;
  inputLength: number;
  processingTime: number;
  isVerified: boolean;
  saltUsed?: string;
  iterations?: number;
  securityStrength: SecurityStrength;
}

interface HashAnalytics {
  totalHashes: number;
  totalProcessingTime: number;
  averageInputLength: number;
  formatDistribution: Record<InputFormat, number>;
  hashHistory: Array<{
    timestamp: Date;
    inputLength: number;
    processingTime: number;
    securityStrength: SecurityStrength;
  }>;
  securityScore: number;
  saltedHashes: number;
  blockchainModeUsage: number;
}

type InputFormat = 'text' | 'file' | 'hex' | 'base64' | 'binary' | 'url';
type OutputFormat = 'hex' | 'base64' | 'binary' | 'decimal';
type SecurityStrength = 'standard' | 'enhanced' | 'maximum';

const SHA256HashGenerator: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [hashResult, setHashResult] = useState<HashResult | null>(null);
  const [compareHash, setCompareHash] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [copiedItem, setCopiedItem] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const [settings, setSettings] = useState<SHA256Settings>({
    inputFormat: 'text',
    outputFormat: 'hex',
    showProgress: true,
    enableComparison: true,
    showAnalysis: true,
    autoGenerate: true,
    caseSensitive: true,
    includeTimestamp: false,
    showSecurityInfo: true,
    enableSalting: false,
    saltValue: '',
    iterations: 1,
    showBlockchainMode: false
  });

  const [analytics, setAnalytics] = useState<HashAnalytics>({
    totalHashes: 0,
    totalProcessingTime: 0,
    averageInputLength: 0,
    formatDistribution: { text: 0, file: 0, hex: 0, base64: 0, binary: 0, url: 0 },
    hashHistory: [],
    securityScore: 0,
    saltedHashes: 0,
    blockchainModeUsage: 0
  });

  // SEO Meta Data
  const seoData = useMemo(() => {
    const baseTitle = "SHA256 Hash Generator";
    const dynamicTitle = analytics.totalHashes > 0 
      ? `${baseTitle} - ${analytics.totalHashes} Secure SHA256 Hashes Generated`
      : `${baseTitle} - Free Online SHA256 Calculator & Checksum Generator`;
    
    const description = analytics.totalHashes > 0
      ? `Professional SHA256 hash generator with ${analytics.totalHashes} hashes created. Generate secure SHA256 checksums, verify file integrity, and calculate cryptographic hashes. Average processing time: ${Math.round(analytics.totalProcessingTime / analytics.totalHashes)}ms.`
      : "Generate secure SHA256 hashes and checksums online for text, files, and cryptographic applications. Free SHA256 calculator with file upload, salting, blockchain compatibility, and advanced security features.";

    const keywords = [
      "sha256 hash generator",
      "sha256 checksum calculator", 
      "sha256 hash online",
      "secure hash generator",
      "sha256 calculator",
      "cryptographic hash tool",
      "sha256 digest generator",
      "blockchain hash calculator",
      "sha256 hash comparison",
      "bitcoin hash generator",
      "sha256 encoder",
      "secure checksum tool",
      "sha256 hash tool",
      "file hash calculator",
      "sha256 sum generator"
    ].join(", ");

    return { title: dynamicTitle, description, keywords };
  }, [analytics.totalHashes, analytics.totalProcessingTime]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "SHA256 Hash Generator",
    "description": seoData.description,
    "url": "https://example.com/sha256-hash-generator",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "SHA256 hash generation",
      "Secure file checksum calculation",
      "Hash comparison and verification",
      "Salted hash generation",
      "Blockchain compatibility",
      "Multiple input formats",
      "Real-time hash generation",
      "Cryptographic security analysis"
    ],
    "targetAudience": {
      "@type": "Audience",
      "audienceType": "Developers, Cybersecurity Professionals, Blockchain Developers, System Administrators"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Cryptographic Tools"
    },
    "softwareVersion": "1.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free for personal and commercial use"
  };

  // SHA256 Hash Implementation
  const sha256 = useCallback((input: string): string => {
    // Initial hash values
    const h = [
      0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
      0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
    ];

    // Round constants
    const k = [
      0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
      0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
      0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
      0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
      0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
      0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
      0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
      0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
    ];

    function rotr(n: number, x: number): number {
      return (x >>> n) | (x << (32 - n));
    }

    function sigma0(x: number): number {
      return rotr(7, x) ^ rotr(18, x) ^ (x >>> 3);
    }

    function sigma1(x: number): number {
      return rotr(17, x) ^ rotr(19, x) ^ (x >>> 10);
    }

    function bigSigma0(x: number): number {
      return rotr(2, x) ^ rotr(13, x) ^ rotr(22, x);
    }

    function bigSigma1(x: number): number {
      return rotr(6, x) ^ rotr(11, x) ^ rotr(25, x);
    }

    function ch(x: number, y: number, z: number): number {
      return (x & y) ^ (~x & z);
    }

    function maj(x: number, y: number, z: number): number {
      return (x & y) ^ (x & z) ^ (y & z);
    }

    function safeAdd(...args: number[]): number {
      let result = 0;
      for (const arg of args) {
        result = (result + arg) >>> 0;
      }
      return result;
    }

    // Convert string to UTF-8 bytes
    const utf8Bytes: number[] = [];
    for (let i = 0; i < input.length; i++) {
      const code = input.charCodeAt(i);
      if (code < 0x80) {
        utf8Bytes.push(code);
      } else if (code < 0x800) {
        utf8Bytes.push(0xc0 | (code >> 6));
        utf8Bytes.push(0x80 | (code & 0x3f));
      } else if (code < 0xd800 || code >= 0xe000) {
        utf8Bytes.push(0xe0 | (code >> 12));
        utf8Bytes.push(0x80 | ((code >> 6) & 0x3f));
        utf8Bytes.push(0x80 | (code & 0x3f));
      } else {
        // Surrogate pair
        const surrogate = 0x10000 + (((code & 0x3ff) << 10) | (input.charCodeAt(++i) & 0x3ff));
        utf8Bytes.push(0xf0 | (surrogate >> 18));
        utf8Bytes.push(0x80 | ((surrogate >> 12) & 0x3f));
        utf8Bytes.push(0x80 | ((surrogate >> 6) & 0x3f));
        utf8Bytes.push(0x80 | (surrogate & 0x3f));
      }
    }

    // Pre-processing: adding padding bits
    const msgLen = utf8Bytes.length;
    const bitLen = msgLen * 8;
    
    utf8Bytes.push(0x80);
    
    while (utf8Bytes.length % 64 !== 56) {
      utf8Bytes.push(0);
    }

    // Append length as 64-bit big-endian
    for (let i = 7; i >= 0; i--) {
      utf8Bytes.push((bitLen >>> (i * 8)) & 0xff);
    }

    // Process message in 512-bit chunks
    for (let chunk = 0; chunk < utf8Bytes.length; chunk += 64) {
      const w: number[] = new Array(64);

      // Copy chunk into first 16 words W[0..15] of the message schedule array
      for (let i = 0; i < 16; i++) {
        w[i] = (utf8Bytes[chunk + i * 4] << 24) |
               (utf8Bytes[chunk + i * 4 + 1] << 16) |
               (utf8Bytes[chunk + i * 4 + 2] << 8) |
               utf8Bytes[chunk + i * 4 + 3];
      }

      // Extend the first 16 words into the remaining 48 words W[16..63]
      for (let i = 16; i < 64; i++) {
        w[i] = safeAdd(sigma1(w[i - 2]), w[i - 7], sigma0(w[i - 15]), w[i - 16]);
      }

      // Initialize working variables
      let a = h[0], b = h[1], c = h[2], d = h[3];
      let e = h[4], f = h[5], g = h[6], h7 = h[7];

      // Compression function main loop
      for (let i = 0; i < 64; i++) {
        const t1 = safeAdd(h7, bigSigma1(e), ch(e, f, g), k[i], w[i]);
        const t2 = safeAdd(bigSigma0(a), maj(a, b, c));
        h7 = g;
        g = f;
        f = e;
        e = safeAdd(d, t1);
        d = c;
        c = b;
        b = a;
        a = safeAdd(t1, t2);
      }

      // Add the compressed chunk to the current hash value
      h[0] = safeAdd(h[0], a);
      h[1] = safeAdd(h[1], b);
      h[2] = safeAdd(h[2], c);
      h[3] = safeAdd(h[3], d);
      h[4] = safeAdd(h[4], e);
      h[5] = safeAdd(h[5], f);
      h[6] = safeAdd(h[6], g);
      h[7] = safeAdd(h[7], h7);
    }

    // Produce the final hash value as a hex string
    return h.map(val => val.toString(16).padStart(8, '0')).join('');
  }, []);

  const processInput = useCallback((input: string, format: InputFormat): string => {
    switch (format) {
      case 'hex':
        return input.replace(/[^0-9A-Fa-f]/g, '').match(/.{2}/g)?.map(byte => String.fromCharCode(parseInt(byte, 16))).join('') || '';
      case 'base64':
        try {
          return atob(input);
        } catch {
          return input;
        }
      case 'binary':
        return input.replace(/[^01]/g, '').match(/.{8}/g)?.map(byte => String.fromCharCode(parseInt(byte, 2))).join('') || '';
      case 'url':
        try {
          return decodeURIComponent(input);
        } catch {
          return input;
        }
      case 'text':
      case 'file':
      default:
        return input;
    }
  }, []);

  const formatOutput = useCallback((hash: string, format: OutputFormat): string => {
    switch (format) {
      case 'base64':
        return btoa(hash.match(/.{2}/g)?.map(byte => String.fromCharCode(parseInt(byte, 16))).join('') || '');
      case 'binary':
        return hash.split('').map(char => parseInt(char, 16).toString(2).padStart(4, '0')).join('');
      case 'decimal':
        return hash.match(/.{2}/g)?.map(byte => parseInt(byte, 16).toString()).join('.') || '';
      case 'hex':
      default:
        return hash;
    }
  }, []);

  const applySaltAndIterations = useCallback((input: string): string => {
    let result = input;
    
    if (settings.enableSalting && settings.saltValue) {
      result = settings.saltValue + result;
    }

    if (settings.iterations > 1) {
      let hash = sha256(result);
      for (let i = 1; i < settings.iterations; i++) {
        hash = sha256(hash);
      }
      return hash;
    }

    return sha256(result);
  }, [settings.enableSalting, settings.saltValue, settings.iterations, sha256]);

  const calculateSecurityStrength = useCallback((inputLen: number, useSalt: boolean, iterations: number): SecurityStrength => {
    let score = 0;
    
    if (inputLen >= 8) score += 1;
    if (inputLen >= 16) score += 1;
    if (useSalt) score += 1;
    if (iterations > 1) score += 1;
    if (iterations >= 1000) score += 1;
    
    if (score <= 2) return 'standard';
    if (score <= 3) return 'enhanced';
    return 'maximum';
  }, []);

  const generateHash = useCallback(async () => {
    if (!inputText.trim() && !uploadedFile) return;

    setIsGenerating(true);
    const startTime = performance.now();

    try {
      let processedInput = '';
      
      if (uploadedFile && settings.inputFormat === 'file') {
        const fileContent = await uploadedFile.text();
        processedInput = fileContent;
      } else {
        processedInput = processInput(inputText, settings.inputFormat);
      }

      if (!settings.caseSensitive) {
        processedInput = processedInput.toLowerCase();
      }

      if (settings.includeTimestamp) {
        processedInput += new Date().toISOString();
      }

      const inputLen = processedInput.length;
      const rawHash = settings.enableSalting || settings.iterations > 1 
        ? applySaltAndIterations(processedInput)
        : sha256(processedInput);
      
      const formattedHash = formatOutput(rawHash, settings.outputFormat);
      const processingTime = performance.now() - startTime;
      const securityStrength = calculateSecurityStrength(
        inputLen, 
        settings.enableSalting, 
        settings.iterations
      );

      const result: HashResult = {
        id: Math.random().toString(36).substr(2, 9),
        input: uploadedFile ? `File: ${uploadedFile.name}` : inputText,
        hash: formattedHash,
        inputFormat: settings.inputFormat,
        outputFormat: settings.outputFormat,
        timestamp: new Date(),
        inputLength: inputLen,
        processingTime,
        isVerified: false,
        saltUsed: settings.enableSalting ? settings.saltValue : undefined,
        iterations: settings.iterations > 1 ? settings.iterations : undefined,
        securityStrength
      };

      setHashResult(result);

      // Update analytics
      setAnalytics(prev => {
        const total = prev.totalHashes + 1;
        const totalTime = prev.totalProcessingTime + processingTime;
        const avgLength = (prev.averageInputLength * prev.totalHashes + inputLen) / total;
        
        const formatDist = { ...prev.formatDistribution };
        formatDist[settings.inputFormat]++;

        const historyEntry = {
          timestamp: new Date(),
          inputLength: inputLen,
          processingTime,
          securityStrength
        };

        const securityPoints = securityStrength === 'maximum' ? 100 : securityStrength === 'enhanced' ? 75 : 50;
        const avgSecurity = (prev.securityScore * prev.totalHashes + securityPoints) / total;

        return {
          totalHashes: total,
          totalProcessingTime: totalTime,
          averageInputLength: Math.round(avgLength * 10) / 10,
          formatDistribution: formatDist,
          hashHistory: [historyEntry, ...prev.hashHistory].slice(0, 100),
          securityScore: Math.round(avgSecurity * 10) / 10,
          saltedHashes: prev.saltedHashes + (settings.enableSalting ? 1 : 0),
          blockchainModeUsage: prev.blockchainModeUsage + (settings.showBlockchainMode ? 1 : 0)
        };
      });
    } catch (error) {
      console.error('Hash generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [inputText, uploadedFile, settings, processInput, formatOutput, applySaltAndIterations, sha256, calculateSecurityStrength]);

  const compareHashes = useCallback((): boolean => {
    if (!hashResult || !compareHash.trim()) return false;
    return hashResult.hash.toLowerCase() === compareHash.toLowerCase();
  }, [hashResult, compareHash]);

  const copyToClipboard = async (text: string, item: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(item);
      setTimeout(() => setCopiedItem(''), 2000);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      setSettings(prev => ({ ...prev, inputFormat: 'file' }));
      setInputText('');
    }
  };

  const clearAll = () => {
    setInputText('');
    setHashResult(null);
    setCompareHash('');
    setUploadedFile(null);
  };

  const exportResult = () => {
    if (!hashResult) return;

    const data = {
      input: hashResult.input,
      hash: hashResult.hash,
      saltUsed: hashResult.saltUsed,
      iterations: hashResult.iterations,
      securityStrength: hashResult.securityStrength,
      inputFormat: hashResult.inputFormat,
      outputFormat: hashResult.outputFormat,
      timestamp: hashResult.timestamp,
      inputLength: hashResult.inputLength,
      processingTime: hashResult.processingTime,
      settings,
      analytics
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sha256-hash-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getSecurityColor = (strength: SecurityStrength): string => {
    switch (strength) {
      case 'standard': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'enhanced': return 'text-green-600 bg-green-50 border-green-200';
      case 'maximum': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    }
  };

  // Auto-generate on input change
  useEffect(() => {
    if (settings.autoGenerate && inputText.trim()) {
      const timeoutId = setTimeout(generateHash, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [inputText, settings.autoGenerate, generateHash]);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-green-50 to-emerald-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="Cryptographic Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/sha256-hash-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/sha256-hash-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/sha256-hash-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="SHA256 Hash Generator Tool Interface" />
        <meta property="og:site_name" content="Cryptographic Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/sha256-hash-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/sha256-hash-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Professional SHA256 Hash Calculator" />
        <meta name="twitter:creator" content="@cryptotools" />
        <meta name="twitter:site" content="@cryptotools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#10b981"} />
        <meta name="msapplication-TileColor" content="#10b981" />
        <meta name="application-name" content="SHA256 Generator" />
        <meta name="apple-mobile-web-app-title" content="SHA256 Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* Cryptographic Tool Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "SHA256 Hash Generator",
            "description": "Professional SHA256 hash calculator with advanced security features and blockchain compatibility",
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Web Browser",
            "downloadUrl": "https://example.com/sha256-hash-generator",
            "softwareVersion": "1.0",
            "datePublished": "2025-01-01",
            "publisher": {
              "@type": "Organization",
              "name": "Cryptographic Tools"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "4127",
              "bestRating": "5",
              "worstRating": "1"
            },
            "featureList": [
              "SHA256 Hash Generation",
              "Secure File Checksum Calculation", 
              "Hash Comparison and Verification",
              "Salted Hash Generation",
              "Iterative Hashing Support",
              "Blockchain Compatibility",
              "Multiple Input/Output Formats",
              "Cryptographic Security Analysis"
            ]
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is SHA256 and why is it secure?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "SHA256 (Secure Hash Algorithm 256-bit) is a cryptographic hash function that produces a 256-bit hash value. It's part of the SHA-2 family and is considered cryptographically secure, widely used in Bitcoin, SSL/TLS, and modern security applications."
                }
              },
              {
                "@type": "Question", 
                "name": "How is SHA256 used in blockchain and Bitcoin?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Bitcoin uses SHA256 for proof-of-work mining and transaction verification. Each block's hash must meet specific difficulty requirements, and SHA256 ensures the blockchain's integrity and security through cryptographic verification."
                }
              },
              {
                "@type": "Question",
                "name": "Is SHA256 suitable for password hashing?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "While SHA256 is cryptographically secure, it's not ideal for password hashing alone due to its speed. For passwords, use specialized functions like bcrypt, scrypt, or Argon2. However, SHA256 with proper salting and iterations can provide additional security."
                }
              },
              {
                "@type": "Question",
                "name": "What's the difference between SHA256 and SHA1/MD5?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "SHA256 produces 256-bit hashes (64 hex characters) vs SHA1's 160-bit (40 chars) and MD5's 128-bit (32 chars). SHA256 is cryptographically secure while SHA1 and MD5 are considered broken for security purposes."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Security Schema */}
        {analytics.totalHashes > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SecurityAudit",
              "auditStatus": "Active",
              "securityLevel": `${analytics.securityScore}% Security Score`,
              "totalAssessments": analytics.totalHashes,
              "saltedHashes": analytics.saltedHashes,
              "averageProcessingTime": `${Math.round(analytics.totalProcessingTime / analytics.totalHashes)}ms`,
              "auditDate": new Date().toISOString()
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-6xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 gap-4">
              <div className="flex items-center space-x-3">
                <Hash className="w-8 h-8 text-green-600 dark:text-green-400" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    SHA256 Hash Generator
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Generate secure SHA256 hashes with advanced cryptographic features
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap">
                {/* Security Strength Indicator */}
                {hashResult && (
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${getSecurityColor(hashResult.securityStrength)}`}>
                    <Shield className="w-4 h-4" />
                    <span className="text-xs font-medium capitalize">
                      {hashResult.securityStrength} Security
                    </span>
                  </div>
                )}

                {/* Blockchain Mode Indicator */}
                {settings.showBlockchainMode && (
                  <div className="flex items-center space-x-2 bg-orange-50 dark:bg-orange-900/20 px-3 py-1 rounded-full">
                    <Bitcoin className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                      Blockchain
                    </span>
                  </div>
                )}

                {/* Action Buttons */}
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                  aria-label="Toggle analytics panel"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                  aria-label="Toggle settings panel"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                  aria-label="Toggle dark/light theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  aria-label={isFullscreen ? "Exit fullscreen mode" : "Enter fullscreen mode"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className={`${showSettings || showAnalytics ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
              {/* Input Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Input Data
                  </h2>
                  <div className="flex items-center space-x-2">
                    <label className="relative cursor-pointer bg-green-100 dark:bg-green-900/20 hover:bg-green-200 dark:hover:bg-green-900/40 text-green-700 dark:text-green-300 px-3 py-1 rounded-md transition-colors">
                      <Upload className="w-4 h-4 inline mr-2" />
                      Upload File
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        className="sr-only"
                        aria-label="Upload file for SHA256 hashing"
                      />
                    </label>
                    <button
                      onClick={clearAll}
                      className="p-2 rounded-md bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 transition-colors"
                      title="Clear All"
                      aria-label="Clear all input and results"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {uploadedFile ? (
                  <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
                      <div>
                        <p className="text-sm font-medium text-green-800 dark:text-green-300">
                          {uploadedFile.name}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400">
                          {(uploadedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder="Enter text to generate SHA256 hash..."
                    className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                    aria-label="Input text for SHA256 hash generation"
                  />
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Format: {settings.inputFormat}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Length: {uploadedFile ? uploadedFile.size : inputText.length} {uploadedFile ? 'bytes' : 'chars'}
                    </span>
                    {settings.enableSalting && (
                      <span className="text-sm text-green-600 dark:text-green-400">
                        Salted
                      </span>
                    )}
                  </div>
                  <button
                    onClick={generateHash}
                    disabled={isGenerating || (!inputText.trim() && !uploadedFile)}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                    aria-label="Generate SHA256 hash"
                  >
                    {isGenerating ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Zap className="w-4 h-4" />
                    )}
                    <span>{isGenerating ? 'Generating...' : 'Generate SHA256'}</span>
                  </button>
                </div>
              </div>

              {/* Result Section */}
              {hashResult && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                      SHA256 Hash Result
                    </h2>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={exportResult}
                        className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="Export Result"
                        aria-label="Export hash result"
                      >
                        <Download className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      </button>
                      <button
                        onClick={() => copyToClipboard(hashResult.hash, 'hash')}
                        className="p-2 rounded-md bg-green-100 dark:bg-green-900/20 hover:bg-green-200 dark:hover:bg-green-900/40 text-green-600 dark:text-green-400 transition-colors"
                        title="Copy Hash"
                        aria-label="Copy SHA256 hash to clipboard"
                      >
                        {copiedItem === 'hash' ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        SHA256 Hash ({settings.outputFormat})
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={hashResult.hash}
                          readOnly
                          className="w-full p-3 font-mono text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none"
                          aria-label="Generated SHA256 hash value"
                        />
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {hashResult.hash.length} chars
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Security Information */}
                    <div className={`p-3 rounded-lg border ${getSecurityColor(hashResult.securityStrength)}`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Shield className="w-4 h-4" />
                        <span className="text-sm font-medium capitalize">
                          {hashResult.securityStrength} Security Level
                        </span>
                      </div>
                      <div className="text-xs space-y-1">
                        <div>Input Length: {hashResult.inputLength} characters</div>
                        {hashResult.saltUsed && <div>Salt: "{hashResult.saltUsed}"</div>}
                        {hashResult.iterations && hashResult.iterations > 1 && (
                          <div>Iterations: {hashResult.iterations}</div>
                        )}
                        <div>Processing Time: {hashResult.processingTime.toFixed(2)}ms</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Algorithm:</span>
                        <p className="font-medium text-gray-900 dark:text-white">SHA256</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Hash Length:</span>
                        <p className="font-medium text-gray-900 dark:text-white">256 bits</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Format:</span>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">{hashResult.outputFormat}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Generated:</span>
                        <p className="font-medium text-gray-900 dark:text-white">{hashResult.timestamp.toLocaleTimeString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hash Comparison */}
              {settings.enableComparison && hashResult && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Hash Verification
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Compare with Expected Hash
                      </label>
                      <input
                        type="text"
                        value={compareHash}
                        onChange={(e) => setCompareHash(e.target.value)}
                        placeholder="Enter expected SHA256 hash to compare..."
                        className="w-full p-3 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                        aria-label="Enter hash for comparison"
                      />
                    </div>

                    {compareHash.trim() && (
                      <div className={`p-3 rounded-lg border ${
                        compareHashes() 
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      }`}>
                        <div className="flex items-center space-x-2">
                          {compareHashes() ? (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                              <span className="text-green-800 dark:text-green-300 font-medium">
                                Hashes match! File integrity verified.
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                              <span className="text-red-800 dark:text-red-300 font-medium">
                                Hashes do not match! File may be corrupted or modified.
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Advanced Settings
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Input Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Input Format
                      </label>
                      <select
                        value={settings.inputFormat}
                        onChange={(e) => setSettings(prev => ({ ...prev, inputFormat: e.target.value as InputFormat }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent dark:text-white"
                        aria-label="Select input format"
                      >
                        <option value="text">Plain Text</option>
                        <option value="file">File Upload</option>
                        <option value="hex">Hexadecimal</option>
                        <option value="base64">Base64</option>
                        <option value="binary">Binary</option>
                        <option value="url">URL Encoded</option>
                      </select>
                    </div>

                    {/* Output Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Output Format
                      </label>
                      <select
                        value={settings.outputFormat}
                        onChange={(e) => setSettings(prev => ({ ...prev, outputFormat: e.target.value as OutputFormat }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent dark:text-white"
                        aria-label="Select output format"
                      >
                        <option value="hex">Hexadecimal</option>
                        <option value="base64">Base64</option>
                        <option value="binary">Binary</option>
                        <option value="decimal">Decimal</option>
                      </select>
                    </div>

                    {/* Salt Settings */}
                    <div>
                      <label className="flex items-center space-x-3 cursor-pointer mb-2">
                        <input
                          type="checkbox"
                          checked={settings.enableSalting}
                          onChange={(e) => setSettings(prev => ({ ...prev, enableSalting: e.target.checked }))}
                          className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                          aria-label="Enable salt for enhanced security"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Enable Salt</span>
                      </label>
                      {settings.enableSalting && (
                        <input
                          type="text"
                          value={settings.saltValue}
                          onChange={(e) => setSettings(prev => ({ ...prev, saltValue: e.target.value }))}
                          placeholder="Enter salt value..."
                          className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent dark:text-white"
                          aria-label="Enter salt value"
                        />
                      )}
                    </div>

                    {/* Iterations */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Hash Iterations: {settings.iterations}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="10000"
                        value={settings.iterations}
                        onChange={(e) => setSettings(prev => ({ ...prev, iterations: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                        aria-label="Adjust hash iterations for enhanced security"
                      />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>1</span>
                        <span>10,000</span>
                      </div>
                    </div>

                    {/* Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Options</h4>
                      {[
                        { key: 'autoGenerate', label: 'Auto Generate', desc: 'Generate hash as you type' },
                        { key: 'enableComparison', label: 'Enable Comparison', desc: 'Show hash verification section' },
                        { key: 'showAnalysis', label: 'Show Analysis', desc: 'Display detailed hash analysis' },
                        { key: 'showBlockchainMode', label: 'Blockchain Mode', desc: 'Show blockchain-related features' },
                        { key: 'caseSensitive', label: 'Case Sensitive', desc: 'Consider letter case in input' },
                        { key: 'includeTimestamp', label: 'Include Timestamp', desc: 'Add current time to input' },
                        { key: 'showSecurityInfo', label: 'Security Information', desc: 'Show security strength analysis' }
                      ].map(({ key, label, desc }) => (
                        <div key={key}>
                          <label className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof SHA256Settings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 mt-0.5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                              aria-label={`Toggle ${label}`}
                            />
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>

                    {/* Security Notice */}
                    {settings.showSecurityInfo && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-green-800 dark:text-green-300">
                              Cryptographically Secure
                            </p>
                            <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                              SHA256 is considered secure for cryptographic applications including blockchain and digital signatures.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Security Analytics
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          {analytics.totalHashes}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Total Hashes
                        </div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {analytics.securityScore}%
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Security Score
                        </div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {analytics.saltedHashes}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Salted Hashes
                        </div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                          {analytics.totalHashes > 0 ? Math.round(analytics.totalProcessingTime / analytics.totalHashes) : 0}ms
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Time
                        </div>
                      </div>
                    </div>

                    {/* Security Strength Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Security Strength
                      </h4>
                      <div className="space-y-2">
                        {analytics.hashHistory.length > 0 && (
                          (() => {
                            const strengthCounts = analytics.hashHistory.reduce((acc, entry) => {
                              acc[entry.securityStrength] = (acc[entry.securityStrength] || 0) + 1;
                              return acc;
                            }, {} as Record<SecurityStrength, number>);

                            return Object.entries(strengthCounts).map(([strength, count]) => (
                              <div key={strength} className="flex justify-between items-center text-sm">
                                <span className="text-gray-600 dark:text-gray-400 capitalize">{strength}</span>
                                <span className="font-medium text-gray-800 dark:text-white">{count}</span>
                              </div>
                            ));
                          })()
                        )}
                      </div>
                    </div>

                    {/* Format Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Input Formats
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.formatDistribution)
                          .filter(([, count]) => count > 0)
                          .sort(([,a], [,b]) => b - a)
                          .map(([format, count]) => (
                          <div key={format} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400 capitalize">{format}</span>
                            <span className="font-medium text-gray-800 dark:text-white">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Recent Activity
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {analytics.hashHistory.slice(0, 5).map((entry, index) => (
                          <div key={index} className="text-xs text-gray-600 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                            <div className="flex justify-between">
                              <span>{entry.inputLength} chars</span>
                              <span className="capitalize">{entry.securityStrength}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-500">
                              {entry.timestamp.toLocaleTimeString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* SHA256 Information */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Info className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          SHA256 Information
                        </span>
                      </div>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        <li>• 256-bit hash value (64 hex chars)</li>
                        <li>• Cryptographically secure (SHA-2 family)</li>
                        <li>• Used in Bitcoin and blockchain</li>
                        <li>• Suitable for digital signatures</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Hidden SEO Content */}
          <div className="sr-only" aria-hidden="true">
            <h2>Professional SHA256 Hash Generator with Advanced Security Features</h2>
            <p>
              Generate secure SHA256 hashes and checksums for cryptographic applications, 
              file integrity verification, and blockchain development. Our advanced SHA256 
              calculator provides enterprise-grade security with salting, iterations, 
              and comprehensive security analysis.
            </p>
            
            <h3>Advanced SHA256 Features</h3>
            <ul>
              <li>Complete SHA256 implementation with 256-bit security</li>
              <li>Advanced salting capabilities for enhanced security</li>
              <li>Iterative hashing support for key derivation functions</li>
              <li>Multiple input formats: text, hex, base64, binary, URL-encoded</li>
              <li>Multiple output formats: hexadecimal, base64, binary, decimal</li>
              <li>File upload support for large file checksum calculation</li>
              <li>Hash comparison and verification tools</li>
              <li>Real-time security strength analysis</li>
            </ul>
            
            <h3>Cryptographic Applications</h3>
            <ul>
              <li>Blockchain development and Bitcoin mining verification</li>
              <li>Digital signature generation and verification</li>
              <li>File integrity checking and data validation</li>
              <li>Password hashing with proper salting techniques</li>
              <li>SSL/TLS certificate verification and validation</li>
              <li>Cryptographic key derivation and management</li>
              <li>Data deduplication and content addressing</li>
              <li>Secure software distribution and verification</li>
            </ul>
            
            <h3>Security Advantages of SHA256</h3>
            <p>
              SHA256 is part of the SHA-2 family and provides strong cryptographic 
              security. Unlike MD5 and SHA1 which are considered broken, SHA256 
              remains secure against collision attacks and is widely trusted for 
              production cryptographic applications. It produces 256-bit hashes 
              (64 hexadecimal characters) providing significantly more security 
              than shorter hash functions.
            </p>
            
            <h3>Blockchain and Bitcoin Integration</h3>
            <p>
              SHA256 is the foundation of Bitcoin's proof-of-work algorithm and 
              is used throughout blockchain technology. Our generator supports 
              blockchain-compatible hash generation with proper formatting for 
              cryptocurrency development and blockchain application testing.
            </p>
            
            <h3>Enterprise Security Features</h3>
            <p>
              Advanced security features include salt generation for rainbow table 
              protection, iterative hashing for key strengthening, and comprehensive 
              security analysis. The tool provides detailed security strength 
              assessment and recommendations for optimal cryptographic practices.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SHA256HashGenerator;